import { format, isBefore, isSameDay } from "date-fns"

import { getTodayCalendarDate } from "@/utils/dateUtil"
import { getInitials as getInitialsHelper, replaceHtmlTagsV2 } from "@/utils/helpers"

import { Notification } from "./Notification"

type ToDoDeadlineStatus = "urgent" | "today" | "past-due" | "scheduled" | "no-date"

export class NotificationTodo extends Notification {
  constructor(props) {
    super(props)
  }

  get toDos() {
    return this.content?.newTodo ?? []
  }

  get projectName() {
    return this.content?.projectName ?? this.toDos[0]?.project?.name
  }

  get initiator() {
    return this.content?.initiator
  }

  get initiatorAvatarSrc() {
    const initiator = this.initiator
    if (!initiator) return ""

    return initiator.picture?.pictureUrl ?? ""
  }

  get initiatorFullName() {
    if (!this.initiator) return ""

    return [this.initiator.firstName, this.initiator.lastName].filter(Boolean).join(" ")
  }

  get todoName() {
    return this.getTodoName(this.content)
  }

  getTodoName(todo) {
    const descriptionHtml = todo?.structuredDescription?.html || ""

    return replaceHtmlTagsV2(descriptionHtml)
      ? descriptionHtml
      : todo?.name || todo?.todoName || todo?.description || ""
  }

  get todoUrl() {
    return this.content?.todoUrl ?? "#"
  }

  get label() {
    return this.content?.label
  }

  get item() {
    return this.content?.item
  }

  get itemLabel() {
    return this.item?.sequenceNumber != null ? `Item #${this.item.sequenceNumber}` : ""
  }

  get itemNumber() {
    return this.item?.sequenceNumber != null ? `#${this.item.sequenceNumber}` : ""
  }

  get itemUrl() {
    return this.content?.relatedUrl ?? this.todoUrl
  }

  get itemTitle() {
    return this.item?.title
  }

  get comment() {
    return this.content?.content ?? ""
  }

  get repliedOnComment() {
    return this.content?.repliedOnComment
  }

  get changeActivityType() {
    return this.content?.changeActivityType
  }

  get commentActivityType() {
    return this.content?.commentActivityType
  }

  get reviewRequestActivityType() {
    return this.content?.reviewRequestActivityType
  }

  get changes() {
    return this.content?.changes ?? {}
  }

  get hasDueDateChange() {
    return Boolean(this.changes.urgent || this.changes.estimatedDeadline)
  }

  get previousDueLabel() {
    if (this.changes.urgent?.oldValue === "true") return "Urgent"

    const previousDeadline = this.changes.estimatedDeadline?.oldValue
    if (this.changes.estimatedDeadline && !previousDeadline) return "No Date"

    return this.formatDeadline(previousDeadline)
  }

  get dueLabel() {
    if (this.content?.urgent) return "Urgent"

    if (this.changes.estimatedDeadline && !this.currentDeadline) return "No Date"

    return this.formatDeadline(this.currentDeadline)
  }

  get dueStatus(): ToDoDeadlineStatus {
    if (this.content?.urgent) return "urgent"

    return this.getDeadlineStatus(this.currentDeadline)
  }

  getInitials() {
    return getInitialsHelper(this.initiatorFullName)
  }

  private formatDeadline(value?: string) {
    if (!value) return ""

    return format(new Date(`${value}T00:00:00`), "d MMM, yyyy")
  }

  private get currentDeadline() {
    return this.changes.estimatedDeadline
      ? this.changes.estimatedDeadline.newValue
      : this.content?.estimatedDeadline
  }

  /** Keeps notification deadline colors aligned with the status rules used by ToDo cards. */
  private getDeadlineStatus(value?: string): ToDoDeadlineStatus {
    if (!value) return "no-date"

    const deadline = new Date(`${value}T00:00:00`)
    const today = getTodayCalendarDate()
    if (isSameDay(deadline, today)) return "today"
    if (isBefore(deadline, today)) return "past-due"
    return "scheduled"
  }

  getProjectId(todo) {
    return { ...todo, projectId: todo.project?.id }
  }
}
